import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DashboardCardId} from 'common/report/util/saved-entities/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import type {Segmentation} from 'common/types/reports/query';

import {ChartType} from '../constants';
import type {DashboardOrigin} from '../util/bookmark-overrides';
import {FeatureRolloutName} from '../util/feature-sampling';

// These values must match template types in webapp/dashboard/models.py
export enum TemplateType {
  KPI = `KPI`,
  FEATURE_LAUNCH = `Feature Launch`,
  LIFECYCLE_COHORT_ANALYSIS = `Lifecycle Cohort Analysis`,
  MARKETING_KPI = `Marketing KPI`,
  E_COMMERCE = `E-Commerce`,
  WEB_ANALYTICS = `Web Analytics`,
  STARTER_BOARD = `Starter Board`,
}

export type TemplateField =
  | KPITemplateFields
  | ProductLaunchTemplateFields
  | LifecycleCohortAnalysisTemplateFields
  | MarketingKPITemplateFields
  | ECommerceTemplateFields
  | WebAnalyticsTemplateFields;

export enum KPITemplateFields {
  SIGN_UP = `Sign Up`,
  VALUE_MOMENT = `Value Moment`,
}

export enum ProductLaunchTemplateFields {
  FEATURE_NAME = `Feature Name`,
  LAUNCH_DATE = `Launch Date`,
  NEW_FEATURE = `New Feature`,
  FEATURE_VALUE_MOMENT = `Feature Value Moment`,
}

export enum LifecycleCohortAnalysisTemplateFields {
  VALUE_MOMENT = `Value Moment`,
}

export enum MarketingKPITemplateFields {
  ACTIVATION_EVENT = `Activation Event`,
  CONVERSION_EVENT = `Conversion Event`,
}

export enum ECommerceTemplateFields {
  PURCHASE_EVENT = `Purchase`,
  ADD_TO_CART_EVENT = `Add to Cart`,
  PRODUCT_VIEWED_EVENT = `Product Viewed`,
  CART_PROPERTY = `Cart Property`,
  CART_PROPERTY_2 = `Cart Property 2`,
  PRICE_KEY = `Price Key`,
  CATEGORY_KEY = `Category Key`,
}

export enum WebAnalyticsTemplateFields {
  PAGE_LOADED_EVENT = `Page Loaded`,
  PAGE_NAME_PROPERTY = `Page Name`,
}

const WHAT_TO_TRACK_URL = `https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track`;

export type TemplateSpecsKeys =
  | TemplateType.KPI
  | TemplateType.FEATURE_LAUNCH
  | TemplateType.LIFECYCLE_COHORT_ANALYSIS
  | TemplateType.MARKETING_KPI
  | TemplateType.E_COMMERCE
  | TemplateType.WEB_ANALYTICS;

export type TemplateSpecsEntry = {
  title: string;
  type: TemplateType;
  templateSelectDescription: string;
  imageId: string;
  eventSelectStepDescription: string;
  demoBoardLink: string;
  moreInfo?: {text: string; link: string};
  requiredFields: TemplateField[];
  keywords: string[];
  rhsDescription?: string;
  requiredFieldsCheck?: () => boolean;
  /**
   * Optionally include a feature flag that's required to display the template.
   */
  featureRolloutName?: FeatureRolloutName;
};

export const TemplateSpecs: Record<TemplateSpecsKeys, TemplateSpecsEntry> = {
  [TemplateType.KPI]: {
    title: `Company KPIs`,
    type: TemplateType.KPI,
    templateSelectDescription: `Nine reports based on core metrics applicable to most products: engagement, retention, and growth.`,
    imageId: `kpi`,
    eventSelectStepDescription: `To use the Company KPI Template, select your corresponding event for each of the suggested events shown.`,
    rhsDescription: `This template is all about your core company metrics and requires two events to get started.`,
    demoBoardLink: `https://mixpanel.com/p/QLBHa24vdYuK2MJLiNUA1S`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [KPITemplateFields.SIGN_UP, KPITemplateFields.VALUE_MOMENT],
    keywords: [
      `Company`,
      `KPI`,
      `KPIs`,
      `Key Performance Indicators`,
      `Engagement`,
      `Retention`,
      `Growth`,
      `Core Performance Indicators`,
      `Core Metrics`,
      `DAU`,
      `MAU`,
      `WAU`,
      `Starter Template`,
      `Template`,
    ],
  },
  [TemplateType.FEATURE_LAUNCH]: {
    title: `Feature Launch`,
    type: TemplateType.FEATURE_LAUNCH,
    templateSelectDescription: `Fourteen reports to help you evaluate adoption, retention and overall product impact of your new feature.`,
    imageId: `feature-launch`,
    eventSelectStepDescription: `To use the Feature Launch Template, fill out details about your feature launch and select your corresponding event for each of the suggested events shown.`,
    rhsDescription: `This template is made up of the most commonly used and actionable product metrics for tracking launches.`,
    demoBoardLink: `https://mixpanel.com/p/5dbqyToAtFMaDJVJNzUeuG`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [
      ProductLaunchTemplateFields.FEATURE_NAME,
      ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT,
      ProductLaunchTemplateFields.LAUNCH_DATE,
      ProductLaunchTemplateFields.NEW_FEATURE,
    ],
    keywords: [
      `Feature`,
      `Launch`,
      `Product Launch`,
      `Feature Release`,
      `Product release`,
      `New Feature`,
      `Launching Feature`,
      `Starter Template`,
      `Template`,
      `Feature adoption`,
      `Activation rate`,
      `Template`,
    ],
  },
  [TemplateType.LIFECYCLE_COHORT_ANALYSIS]: {
    title: `Lifecycle Cohort Analysis`,
    type: TemplateType.LIFECYCLE_COHORT_ANALYSIS,
    templateSelectDescription: `Ten reports to help you breakdown different users and their journey using your product.`,
    imageId: `lifecycle-cohort-analysis`,
    eventSelectStepDescription: `To use the Lifecycle Cohort Analysis Template, fill out your value moment so that we can uncover what causes change in your user base and can help you build more sustainable user growth.`,
    rhsDescription: `This template is about uncovering changes in your user base and building more sustainable user growth.`,
    demoBoardLink: `https://mixpanel.com/p/CEngFwLvPa5zvTSLvFTHNH`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [LifecycleCohortAnalysisTemplateFields.VALUE_MOMENT],
    keywords: [
      `LCA`,
      `Lifecycle`,
      `Cohort`,
      `Cohort analysis`,
      `User template`,
      `User journey`,
      `User segmentation`,
      `Value Moment`,
      `Value Moment Analysis`,
      `Resurrected User`,
      `Dormant user`,
      `Retained user`,
      `new user`,
      `Template`,
    ],
  },
  [TemplateType.MARKETING_KPI]: {
    title: `Marketing KPIs`,
    type: TemplateType.MARKETING_KPI,
    templateSelectDescription: `Fifteen reports to help you optimize your marketing strategy.`,
    imageId: `marketing-kpi`,
    eventSelectStepDescription: `To use the Marketing KPI Template, enter up to two events: what makes an active user and the action you want users to take.`,
    rhsDescription: `This template is made up of the core metrics to help you optimize your marketing strategy.`,
    demoBoardLink: `https://mixpanel.com/p/MKDgQSoYBZciN4AGgY7Mgh`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [MarketingKPITemplateFields.CONVERSION_EVENT],
    keywords: [
      `Marketing`,
      `KPIs`,
      `Key Performance Indicators`,
      `User base`,
      `Marketing Campaign`,
      `Activation`,
      `Conversion`,
      `Acquisition`,
      `Session engagement`,
      `Conversion journey`,
      `Template`,
    ],
  },
  [TemplateType.E_COMMERCE]: {
    title: `Ecommerce`,
    type: TemplateType.E_COMMERCE,
    templateSelectDescription: `Eighteen reports to provide common strategic plans and goals of an ecommerce business.`,
    eventSelectStepDescription: `The Ecommerce template requires object properties underneath two events. If you don't have an object property to represent your shopping cart, learn how to set it up`,
    rhsDescription: `To use this template we require three events and four properties.`,
    imageId: `e-commerce`,
    demoBoardLink: `https://mixpanel.com/p/2cv24kB3k5n9reXr9bSJKU`,
    moreInfo: {
      text: `here.`,
      link: `https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#list-of-objects-property-support`,
    },
    requiredFields: [
      ECommerceTemplateFields.ADD_TO_CART_EVENT,
      ECommerceTemplateFields.CART_PROPERTY,
      ECommerceTemplateFields.CATEGORY_KEY,
      ECommerceTemplateFields.PRICE_KEY,
      ECommerceTemplateFields.PRODUCT_VIEWED_EVENT,
      ECommerceTemplateFields.PURCHASE_EVENT,
    ],
    keywords: [`Ecommerce`, `E-Commerce`, `Purchase`, `Retail`, `Cart analysis`, `Template`],
  },
  [TemplateType.WEB_ANALYTICS]: {
    title: `Web Analytics`,
    type: TemplateType.WEB_ANALYTICS,
    templateSelectDescription: `Sixteen reports to evaluate core user, session and page metrics.`,
    eventSelectStepDescription: `To use the Web Analytics Template, fill out a few details below about your website.`,
    imageId: `web-analytics`,
    demoBoardLink: `https://mixpanel.com/project/2979683/view/3498935/app/boards/#id=5726951`,
    requiredFields: [WebAnalyticsTemplateFields.PAGE_LOADED_EVENT, WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY],
    keywords: [`Web Analytics`, `Website Metrics`, `Template`],
    featureRolloutName: FeatureRolloutName.WebAnalyticsDashboardAndChannelClassifier,
  },
};

type CreatorEditorInfo = {
  created?: string;
  creator_id: null | number;
  creator_name: null | string; // full name of creator
  creator_email: null | string;

  modified?: string;
  last_modified_by_id?: number;
  last_modified_by_email?: string;
  last_modified_by_name?: string;
};

// Bookmark format returned by the dashboards and bookmarks API
export type Bookmark<BookmarkParamsType = TODO> = CreatorEditorInfo & {
  dashboard: DashboardOrigin;
  id: number;
  is_default: boolean;
  name: string;
  description?: string;
  original_type: string;
  params: BookmarkParamsType;
  project_id: number;
  icon?: string;
  workspace_id?: number;
  type: BookmarkType;
  error?: string;

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
};

// the only necessary fields needed to persist
export type DisplayBookmark<BookmarkParamsType = any> = Pick<
  Bookmark<BookmarkParamsType>,
  'params' | 'type' | 'name' | 'description'
>;

export interface DashboardCardChartOptions {
  type?: ChartType;
}

export type CardOnDashboard = DashboardCard | TextCard | MediaCard | NestedDashboardCard | ListCard | ComponentCard;

export type SeenCards = Record<DashboardCardId, boolean>;

//TODO @shayan, since we added content_id, remove duplicate id types from cards after GA

// report or report link type card on the dashboard
export interface DashboardCard<BookmarkParamsType = TODO> {
  id: DashboardCardId;
  title?: string;
  description?: string;
  width: number;
  height: number;
  bookmark?: Bookmark<BookmarkParamsType>;
  bookmark_metadata?: any;
  chart_options?: DashboardCardChartOptions;
  linked_dashboard_card_id?: number;
  type?: Exclude<ContentType, ContentType.AIHelper>;
  source_bookmark_id?: number;
  content_id?: number;
}

export interface NestedDashboardCard {
  id: DashboardCardId;
  width: number;
  height: number;
  type: ContentType.Dashboard;
  content_id: number;
  title: string;
  description: string;
  last_edited: string;
  thumbnail_layout: LayoutResponse;
  thumbnail_contents: ContentsResponse;
}

export interface TextCard {
  id: DashboardCardId;
  width: number;
  height: number;
  markdown: string;
  type?: Exclude<ContentType, ContentType.AIHelper>;
  text_card_id?: number;
  content_id?: number;
}

export interface ListCard {
  id: DashboardCardId;
  width: number;
  height: number;
  title: string;
  columns: string[];
  type?: Exclude<ContentType, ContentType.AIHelper>;
  list_card_id?: number;
  content_id?: number;
}

export interface ComponentCard {
  id: DashboardCardId;
  width: number;
  height: number;
  component_subtype: string;
  type: ContentType.Component;
  component_card_id?: number;
  content_id?: number;
}

export enum ContentType {
  ReportLink = `report-link`,
  Report = `report`,
  Text = `text`,
  Media = `media`,
  Dashboard = `dashboard`,
  // Special frontend only card. It always lives in a row alone. It is filtered out when saving.
  AIHelper = `ai-helper`,
  // TODO(Home)Remove or move once the Home prototype has finished running.
  List = `list`, // only for home-prototype
  Component = `component`, // only for home-prototype
}

export enum ContentActionType {
  Move = `move`,
  Delete = `delete`,
  Duplicate = `duplicate`, // for report/report-links, creates corresponding report
  Create = `create`,
  Update = `update`,
  Undelete = `undelete`,
  UnMove = `unmove`,
  CreateReportLink = `create-report-link`, // only for report/report-links
}

export interface ContentAction {
  actionType: ContentActionType;
  contentId?: number;
  contentType: ContentType;
  contentParams?: TODO;
}

/*
  Must match MediaCardType in dashboard/models.py
*/
export enum MediaCardType {
  Image = `image`,
  Video = `video`,
  EmbeddedImage = `embed_image`,
  EmbeddedVideo = `embed_video`,
}

export interface MediaCard {
  id: DashboardCardId;
  width: number;
  height: number;
  type?: Exclude<ContentType, ContentType.AIHelper>;
  media_card_id?: number;
  media_type: MediaCardType;
  service: string;
  path: string;
  content_id?: number;
}

export enum TextCardAlignmentType {
  Left = `left`,
  Right = `right`,
  Center = `center`,
}

export const TextCardAlignmentConfig = {
  [TextCardAlignmentType.Center]: {
    icon: `align-center`,
    label: `Align center`,
    value: TextCardAlignmentType.Center,
  },
  [TextCardAlignmentType.Left]: {
    icon: `align-left`,
    label: `Align left`,
    value: TextCardAlignmentType.Left,
  },
  [TextCardAlignmentType.Right]: {
    icon: `align-right`,
    label: `Align right`,
    value: TextCardAlignmentType.Right,
  },
};

export enum DashboardType {
  UserCreated = `User Created`,
  TemplateGenerated = `Template Generated`,
  MyDashboard = `My Dashboard`,
  StagedTemplate = `Staged Template`,
}

export type Dashboard = CreatorEditorInfo & {
  id: number;
  title: string;
  description: null | string;
  bookmarks?: Bookmark[];
  card_order: DashboardCardId[];
  cards: CardOnDashboard[];
  time_filter: TimeFilter;
  filters: Segmentation[];
  generation_type?: DashboardType;
  template_type?: TemplateType;

  // permissions
  can_view: boolean;
  can_update_basic: boolean;
  can_share: boolean;
  is_superadmin: boolean;
  allow_staff_override: boolean;
  can_pin_dashboards?: boolean;

  is_favorited?: boolean;
  pinned_date?: string;
  num_active_public_links?: number;
  project_id?: number;
  is_shared_with_project?: boolean;

  layout: LayoutResponse;
  contents: ContentsResponse;

  parent_dashboard_id?: number;
  ancestors?: Array<Pick<Dashboard, 'id' | 'title'>>;
  child_dashboards?: Dashboard[];
};

export type TimeFilter =
  | {
      displayText: string;
      dateRange: DateRange;
    }
  | EmptyObj;

export enum DashboardQueryOrigin {
  Standard = `dashboard`,
  Digest = `dashboard_digest`,
  Public = `dashboard_public`,
}

export enum BadLayoutRequestErrors {
  // the string values are defined in app_api
  InvalidLayout = `INVALID_LAYOUT`,
  TooManyMediaCardsOnDashboard = `TOO_MANY_DASH_MEDIA_CARDS`,
  TooManyMediaCardsInProject = `TOO_MANY_PROJECT_MEDIA_CARDS`,
}

export const BadLayoutRequestMessages = {
  [BadLayoutRequestErrors.InvalidLayout]: `Changes could not be saved due to a change conflict.`,
  [BadLayoutRequestErrors.TooManyMediaCardsOnDashboard]: `Changes could not be saved. This board has reached its media card limit.`,
  [BadLayoutRequestErrors.TooManyMediaCardsInProject]: `Changes could not be saved. This project has reached its media card limit.`,
};

export type LayoutCell = {
  id: string;
  content_id: string;
  content_type: ContentType;
  width: number;
};

export interface LayoutRowResponse {
  height: number;
  cells: LayoutCell[];
}

export type LayoutResponse =
  | EmptyObj
  | {
      version: string;
      order: string[];
      rows: {[id: string]: LayoutRowResponse};
    };

type BaseContent = {
  creator: string;
  creator_id: number;
  creator_name: string;
  creator_email: string;
  dashboard_id: number;
  id: number;
};

export type MediaContent = BaseContent & {
  media_type: MediaCardType;
  path: string;
  service: string;
};

export type TextContent = BaseContent & {
  markdown: string;
};

export type ReportContent = Bookmark;

export type ReportLinkContent = {
  id: number;
  bookmark: Bookmark;
};

export type NestedDashboardContent = Pick<
  Dashboard,
  'id' | 'description' | 'last_modified_by_email' | 'last_modified_by_id' | 'last_modified_by_name' | 'creator_id'
> & {last_edited: string; thumbnail_layout: LayoutResponse; thumbnail_contents: ContentsResponse};

export type ContentsMapById<Content> = {
  [id: number]: Content;
};

export type ContentsResponse =
  | EmptyObj
  | {
      [ContentType.Media]: ContentsMapById<MediaContent>;
      [ContentType.Text]: ContentsMapById<TextContent>;
      [ContentType.Report]: ContentsMapById<ReportContent>;
      [ContentType.ReportLink]: ContentsMapById<ReportLinkContent>;
      [ContentType.Dashboard]: ContentsMapById<NestedDashboardContent>;
    };

export type CardContent = ReportContent | ReportLinkContent | TextContent | MediaContent;
